// import { EMAIL_VALIDATE_REGEX } from '@/configs/constants';
import { Button } from '@/components/common/Button';
import { useStore } from '@/hooks/useStore';
import { toastify } from '@/utils/toastify';
import { cn } from '@/utils/utils';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_PUBKEY;
type Props = {
    setAddressSelected: (value: { address: string, latitude: string, longitude: string }) => void
};
export default observer(function MapForm({ setAddressSelected }: Props) {
    // hooks
    const { t } = useTranslation();

    // store
    const {
        modalStore: { hideModal }
    } = useStore();

    // state
    const mapContainer = useRef<HTMLDivElement>(null);
    const map = useRef<mapboxgl.Map | null>(null);
    const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
    const marker = useRef<mapboxgl.Marker | null>(null);

    const [selectedLocation, setSelectedLocation] = useState<[number, number] | null>(null);
    const [address, setAddress] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        if ('geolocation' in navigator) {
            const watchId = navigator.geolocation.watchPosition(
                (position) => {
                    const { longitude, latitude } = position.coords;
                    const newLocation: [number, number] = [longitude, latitude];
                    if (
                        !userLocation ||
                        Math.abs(newLocation[0] - userLocation[0]) > 0.00001 ||
                        Math.abs(newLocation[1] - userLocation[1]) > 0.00001
                    ) {
                        setUserLocation(newLocation);
                    }
                },
                (error) => {
                    hideModal();
                    toastify('error', '位置エラー。現在の場所へのアクセスを許可してください');
                },
                {
                    enableHighAccuracy: true,
                    maximumAge: 0,
                    timeout: 10000
                }
            );

            return () => navigator.geolocation.clearWatch(watchId);
        } else {
            toastify('error', 'このブラウザでは地理位置情報がサポートされていません');
        }
    }, []);

    useEffect(() => {
        if (map.current) {
            return; // Tránh khởi tạo lại bản đồ nếu đã có
        }

        if (userLocation && mapContainer.current) {
            // Khởi tạo bản đồ
            map.current = new mapboxgl.Map({
                container: mapContainer.current,
                style: 'mapbox://styles/mapbox/streets-v12',
                center: selectedLocation || userLocation,
                zoom: 16,
                minZoom: 14,
                maxZoom: 18
            });

            map.current.on('load', () => {
                // Thêm nguồn dữ liệu cho bản đồ
                map.current?.addSource('circleSource', {
                    type: 'geojson',
                    data: {
                        type: 'FeatureCollection',
                        features: [
                            {
                                type: 'Feature',
                                geometry: {
                                    type: 'Point',
                                    coordinates: userLocation
                                },
                                properties: {}
                            }
                        ]
                    }
                });

                // Thêm lớp hình tròn trên bản đồ
                map.current?.addLayer({
                    id: 'gpsArea',
                    type: 'circle',
                    source: 'circleSource',
                    paint: {
                        'circle-color': '#007bff',
                        'circle-opacity': 1
                    }
                });
            });

            // Tạo marker có thể kéo
            marker.current = new mapboxgl.Marker({
                draggable: true,
                color: '#FF0000'
            })
                .setLngLat(userLocation)
                .addTo(map.current);

            // Địa chỉ mặc định ban đầu
            const popup = new mapboxgl.Popup({
                offset: [0, -38],
                closeButton: false,
                closeOnClick: false,
                maxWidth: '300px'
            }).setHTML(`
                <div style="font-size: 15px;">
                    <p style="font-weight: 500; color: #1A1A1A;">${address}</p>
                    <p style="color: #34A853; font-size: 13px; margin-top: 5px;">${userLocation[1]}, ${userLocation[0]}</p>
                </div>
            `);

            popup.on('open', () => {
                const popupContent = document.querySelector('.mapboxgl-popup-content') as HTMLElement;
                if (popupContent) {
                    popupContent.style.boxShadow = '0px 8px 32px 0px rgba(0, 0, 0, 0.15)';
                    popupContent.style.borderRadius = '8px';
                    popupContent.style.padding = '16px';
                }
            });

            marker.current.setPopup(popup).togglePopup();

            // Gọi API để lấy địa chỉ ban đầu
            getAddressFromCoordinates(userLocation[0], userLocation[1]);

            // Khi kéo marker xong, cập nhật tọa độ và địa chỉ
            marker.current.on('dragend', () => {
                const lngLat = marker.current!.getLngLat();
                setSelectedLocation([lngLat.lng, lngLat.lat]);

                // Cập nhật địa chỉ mới
                getAddressFromCoordinates(lngLat.lng, lngLat.lat);

                // Cập nhật popup với địa chỉ mới
                popup.setHTML(`
                    <div style="font-size: 15px;">
                        <p style="font-weight: 500; color: #1A1A1A;">${address}</p>
                        <p style="color: #34A853; font-size: 13px; margin-top: 5px;">${lngLat.lat}, ${lngLat.lng}</p>
                    </div>
                `);
                marker.current!.setPopup(popup).togglePopup();
            });
        }
    }, [userLocation, selectedLocation, address]); // Cập nhật khi userLocation hoặc selectedLocation thay đổi

    // Đảm bảo address được cập nhật sau khi lấy từ API
    useEffect(() => {
        if (marker.current && address && selectedLocation) {
            const popup = marker.current.getPopup();
            if (popup) {
                popup.setHTML(`
                    <div style="font-size: 15px;">
                        <p style="font-weight: 500; color: #1A1A1A;">${address}</p>
                        <p style="color: #34A853; font-size: 13px; margin-top: 5px;">${selectedLocation[1]}, ${selectedLocation[0]}</p>
                    </div>
                `);
                marker.current.setPopup(popup).togglePopup();
            }
        }
    }, [userLocation, selectedLocation, address]);

    const getAddressFromCoordinates = async (lng: number, lat: number) => {
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
            );
            const data = await response.json();
            if (data && data.display_name) {
                setIsLoading(false);
                setAddress(data.display_name);
                setSelectedLocation([lng, lat]);
            } else {
                setAddress('住所が見つかりません');
                setIsLoading(true);
            }
        } catch (error) {
            setAddress('アドレス取得エラー');
        }
    };

    const confirmLocation = () => {
        const location = selectedLocation || userLocation;
        if (location) {
            setAddressSelected({
                address,
                latitude: location[1].toString(),
                longitude: location[0].toString()
            });
            hideModal();
        }
    };

    return (
        <>
            <div className='w-full'>
                <div ref={mapContainer} className='w-full h-[493px]' />
                <div className='mt-6 flex justify-center items-center space-x-[10px]'>
                    <Button
                        type='button'
                        variant='outline'
                        className={cn('w-[166.5px] text-[15px] font-semibold h-12 hover:text-text-black-1 border border-border-button text-text-black-1 !rounded-[10px]')}
                        onClick={() => hideModal()}
                    >
                        {t('button.cancel_2')}
                    </Button>
                    <Button
                        disabled={isLoading}
                        onClick={() => confirmLocation()}
                        type='submit'
                        variant='active'
                        className={cn('w-[166.5px] text-[15px] font-semibold h-12 bg-active hover:opacity-80 hover:text-white transition-opacity duration-300 !rounded-[10px]')}
                    >
                        {t('button.save')}
                    </Button>
                </div>
            </div>
        </>
    );
});
